import React from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Chip,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  keyframes,
} from "@mui/material";
import { useColorMode } from "../../context/ColorModeContext";
import { useLayout } from "../../context/LayoutContext";
import { navRailWidth } from "./navRail";
import LargeLogoWithTextDark from "../../assets/Lockup Horizontal Dark.svg";
import LargeLogoWithTextLight from "../../assets/Lockup Horizontal Light.svg";
import { useNavigate, useLocation } from "react-router-dom";


// Three dots that bounce in sequence (staggered animation-delay) to form a
// wave — the loading indicator shown in place of a not-yet-known count.
const waveAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.45;
  }
  30% {
    transform: translateY(-3px);
    opacity: 1;
  }
`;

const TopAppBar: React.FC = () => {
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const location = useLocation();
  const { pageHeader, subNavItems, sidebarOpen } = useLayout();
  const { mode } = useColorMode();
  const currentPath = location.pathname.replace(/\/$/, "");

  const handleLogoClick = () => {
    navigate("/");
  };





  // Track the rail so the page header starts exactly at the content column's
  // left edge. This was a hardcoded 240 against a 232px rail, which put the
  // header 8px into the content on open and 176px adrift on collapse. The
  // block already has `overflow: hidden` and a width transition for precisely
  // this -- it was written to follow a collapsing rail and was simply never
  // connected to one.
  const brandingWidth = navRailWidth(sidebarOpen);

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={1}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        bgcolor: "background.paper",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar sx={{ pl: { lg: 0 } }}>

        {/* Logo - hidden on xs screens to prevent clipping */}
        {isSmUp && (
          <Box
            onClick={handleLogoClick}
            sx={{
              width: isLgUp ? brandingWidth : "auto",
              pl: { lg: 3 }, // Restore visual padding inside container (24px)
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              overflow: "hidden", // Ensures logo clips cleanly when collapsed
              transition: "width 0.2s ease-in-out",
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              sx={{
                height: isLgUp ? 32 : 24,
                width: "auto",
                transition: "transform 0.2s ease-in-out",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
              alt="Logo"
              src={mode === 'dark' ? LargeLogoWithTextLight : LargeLogoWithTextDark}
            />
          </Box>
        )}

        {/* Page Header or Spacer */}
        <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", ml: 2 }}>
          {pageHeader && (
            <>
              {/* Single-line header: the title owns the line at full size;
                  the subtitle sits beside it and is the part that shrinks
                  and ellipsizes when space runs out. The icon lives OUTSIDE
                  the text row: it is vertically centered, while title and
                  subtitle align on their shared text baseline — flex
                  baseline alignment only works when every item in the row
                  is plain text. */}
              <Box
                sx={{
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {pageHeader.icon && (
                  <Box
                    component="span"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      color: "primary.main",
                      "& > svg": { fontSize: 22 },
                    }}
                  >
                    {pageHeader.icon}
                  </Box>
                )}
                <Box
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    alignItems: "baseline",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontSize: "1.15rem",
                      lineHeight: 1.3,
                      color: "text.primary",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {pageHeader.title}
                  </Typography>
                  {pageHeader.subtitle && isLgUp && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {pageHeader.subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
              {pageHeader.chip && (
                <Chip
                  label={pageHeader.chip.loading ? (
                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                      <Box
                        component="span"
                        aria-hidden="true"
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "2px",
                          mr: 0.75,
                          "& > span": {
                            width: "3px",
                            height: "3px",
                            borderRadius: "50%",
                            backgroundColor: "currentColor",
                            animation: `${waveAnimation} 1.2s ease-in-out infinite`,
                          },
                          "& > span:nth-of-type(2)": { animationDelay: "0.15s" },
                          "& > span:nth-of-type(3)": { animationDelay: "0.3s" },
                        }}
                      >
                        <span />
                        <span />
                        <span />
                      </Box>
                      {pageHeader.chip.label}
                    </Box>
                  ) : (
                    pageHeader.chip.label
                  )}
                  icon={!pageHeader.chip.loading && pageHeader.chip.icon ? (
                    <Box component="span" sx={{ display: "flex", alignItems: "center", "& > svg": { fontSize: 16 } }}>
                      {pageHeader.chip.icon}
                    </Box>
                  ) : undefined}
                  size="small"
                  sx={{
                    ml: 2,
                    fontWeight: 600,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "& .MuiChip-icon": {
                      color: "inherit",
                    },
                  }}
                />
              )}
            </>
          )}
        </Box>

        {/* Sub-navigation tabs */}
        {subNavItems.length > 0 && (
          <Tabs
            value={
              subNavItems.findIndex(
                (item) => currentPath === item.path.replace(/\/$/, "")
              ) === -1
                ? false
                : subNavItems.findIndex(
                    (item) => currentPath === item.path.replace(/\/$/, "")
                  )
            }
            onChange={(_, newValue) => navigate(subNavItems[newValue].path)}
            sx={{
              minHeight: 48,
              ml: 2,
              "& .MuiTabs-indicator": {
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.875rem",
                minHeight: 48,
                px: 2,
                gap: 0.75,
                "&.Mui-selected": {
                  fontWeight: 600,
                },
              },
            }}
          >
            {subNavItems.map((item) => (
              <Tab
                key={item.path}
                label={item.label}
                icon={
                  item.icon ? (
                    <Box
                      component="span"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        "& > svg": { fontSize: 18 },
                      }}
                    >
                      {item.icon}
                    </Box>
                  ) : undefined
                }
                iconPosition="start"
              />
            ))}
          </Tabs>
        )}

      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;
